import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface FAQItem {
  icon: string;
  question: string;
  answer: string | string[];
}

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqData: FAQItem[] = [
    {
      icon: "❓",
      question: "Do I need prior music training to join?",
      answer: "No. This community is designed for first-time original music creators. You don't need formal training, deep music theory, or years of practice. Everything is taught step by step, practically."
    },
    {
      icon: "🎸",
      question: "I don't play any instrument. Can I still create music?",
      answer: "Yes. Many members start without playing an instrument. You'll learn how to use your voice, simple tools, and modern software to compose and build your first song."
    },
    {
      icon: "🧠",
      question: "Is music theory compulsory?",
      answer: "No heavy theory is required. We focus only on practical concepts that help you create, not academic theory that confuses beginners."
    },
    {
      icon: "🏠",
      question: "Can I do this from home?",
      answer: "Absolutely. The system is designed for home creators using basic equipment and accessible software. A professional studio is not required."
    },
    {
      icon: "⏱️",
      question: "How much time do I need to invest weekly?",
      answer: "On average, 3–5 hours per week is enough if you stay consistent. You can move at your own pace since the content is self-paced."
    },
    {
      icon: "🚀",
      question: "Will I really release my first original song in 90 days?",
      answer: "Yes—if you follow the process and complete the assignments. The 90-day framework provides structure and direction, but your results depend on your commitment."
    },
    {
      icon: "🎧",
      question: "Will I learn music production too?",
      answer: "Yes. From the Foundational level onwards, you'll learn basic to advanced music production concepts, including vocals, arrangement, and sound selection."
    },
    {
      icon: "📱",
      question: "Will you teach how to launch and distribute songs?",
      answer: "Yes. You'll learn the basics of releasing your song, branding yourself as an indie artist, and taking your first steps toward visibility and monetization."
    },
    {
      icon: "🤝",
      question: "Is this a course or a community?",
      answer: "It's both. You get structured learning + a supportive community where you can interact, collaborate, and grow with fellow creators."
    },
    {
      icon: "🎯",
      question: "Who is this NOT for?",
      answer: [
        "This is not for:",
        "• People looking for overnight fame",
        "• Those unwilling to practice or take action",
        "• Anyone expecting shortcuts without effort",
        "",
        "This is for creators ready to learn, apply, and grow."
      ]
    },
    {
      icon: "🔄",
      question: "Can I upgrade my membership later?",
      answer: "Yes. You can start at a level that suits you today and upgrade as your skills and confidence grow."
    },
    {
      icon: "🎵",
      question: "What age group is this best suited for?",
      answer: 
      [
        "There's no age bar to creating music. This community is for anyone who genuinely wants to learn, create, and release original music—at their own pace.",
        "",
        "Best suited for:",
        "👉 Teens eager to explore music creation",
        "👉 Adults seeking a new creative outlet",
        "👉 Seniors wanting to express themselves through music",
        "",
        "💜 If you have the passion, IMC community welcomes you."
      ]
    },
    {
      icon: "🙋",
      question: "I'm not confident about my voice. Should I still join?",
      answer: "Yes. Confidence grows through creation, not before it. Many members join unsure of themselves and gain clarity and confidence as they progress."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 px-6 bg-gradient-to-br from-purple-50 to-blue-50">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h2>
          <div className="w-24 h-1 bg-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">
            Get answers to common questions about the music creation journey
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqData.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl shadow-md overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-start justify-between text-left hover:bg-purple-50 transition-colors duration-200"
              >
                <div className="flex items-start gap-4 flex-1">
                  <span className="text-2xl flex-shrink-0">{faq.icon}</span>
                  <span className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </span>
                </div>
                <motion.svg
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-6 h-6 text-purple-600 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </motion.svg>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-5 pl-16">
                      {Array.isArray(faq.answer) ? (
                        <div className="text-gray-700 leading-relaxed space-y-1">
                          {faq.answer.map((line, i) => (
                            <p 
                              key={i}
                              className={line.trim().startsWith('👉') ? 'pl-6' : ''}
                            >
                              {line.trim()}
                            </p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-700 leading-relaxed">
                          {faq.answer}
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              💬 Still have questions?
            </h3>
            <p className="text-gray-600 mb-6">
              You're welcome to reach out or explore the community to see if it feels right for you.
            </p>
            <a
              href="/zoom"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200"
            >
              👉 Start Your Music Journey
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
